export type MarkdownFormatResult = { text: string; start: number; end: number };

function lineRange(text: string, start: number, end: number) {
  const lineStart = text.lastIndexOf('\n', start - 1) + 1;
  const nextBreak = text.indexOf('\n', end);
  const lineEnd = nextBreak === -1 ? text.length : nextBreak;
  return { lineStart, lineEnd };
}

export function toggleWrap(
  text: string,
  start: number,
  end: number,
  prefix: string,
  suffix: string = prefix
): MarkdownFormatResult {
  const selected = text.slice(start, end);

  const outerBefore = text.slice(Math.max(0, start - prefix.length), start);
  const outerAfter = text.slice(end, end + suffix.length);
  if (selected.length > 0 && outerBefore === prefix && outerAfter === suffix) {
    const next = text.slice(0, start - prefix.length) + selected + text.slice(end + suffix.length);
    return { text: next, start: start - prefix.length, end: end - prefix.length };
  }

  const isWrappedInside =
    selected.length >= prefix.length + suffix.length &&
    selected.startsWith(prefix) &&
    selected.endsWith(suffix);
  if (isWrappedInside) {
    const inner = selected.slice(prefix.length, selected.length - suffix.length);
    const next = text.slice(0, start) + inner + text.slice(end);
    return { text: next, start, end: start + inner.length };
  }

  const next = text.slice(0, start) + prefix + selected + suffix + text.slice(end);
  return { text: next, start: start + prefix.length, end: start + prefix.length + selected.length };
}

export function toggleLinePrefix(
  text: string,
  start: number,
  end: number,
  prefix: string
): MarkdownFormatResult {
  const { lineStart, lineEnd } = lineRange(text, start, end);
  const block = text.slice(lineStart, lineEnd);
  const lines = block.split('\n');
  const contentLines = lines.filter(line => line.length > 0);
  const allPrefixed = contentLines.length > 0 && contentLines.every(line => line.startsWith(prefix));

  const nextLines = allPrefixed
    ? lines.map(line => (line.startsWith(prefix) ? line.slice(prefix.length) : line))
    : contentLines.length === 0
      ? lines.map(line => prefix + line)
      : lines.map(line => (line.length > 0 ? prefix + line : line));

  const nextBlock = nextLines.join('\n');
  const next = text.slice(0, lineStart) + nextBlock + text.slice(lineEnd);
  const delta = nextBlock.length - block.length;
  const firstLineDelta = allPrefixed ? -prefix.length : prefix.length;
  return {
    text: next,
    start: Math.max(lineStart, start + firstLineDelta),
    end: end + delta
  };
}

export function toggleOrderedList(text: string, start: number, end: number): MarkdownFormatResult {
  const { lineStart, lineEnd } = lineRange(text, start, end);
  const block = text.slice(lineStart, lineEnd);
  const lines = block.split('\n');
  const contentLines = lines.filter(line => line.length > 0);
  const isOrdered = contentLines.length > 0 && contentLines.every(line => /^\d+\.\s/.test(line));

  let counter = 1;
  const numberLine = (line: string) => {
    const numbered = `${counter}. ${line}`;
    counter += 1;
    return numbered;
  };
  const nextLines = isOrdered
    ? lines.map(line => line.replace(/^\d+\.\s/, ''))
    : contentLines.length === 0
      ? lines.map(numberLine)
      : lines.map(line => (line.length === 0 ? line : numberLine(line)));

  const nextBlock = nextLines.join('\n');
  const next = text.slice(0, lineStart) + nextBlock + text.slice(lineEnd);
  const delta = nextBlock.length - block.length;
  return { text: next, start: lineStart, end: end + delta };
}

export function toggleCodeBlock(text: string, start: number, end: number): MarkdownFormatResult {
  const fence = '```';
  const selected = text.slice(start, end);
  const isFenced =
    selected.startsWith(`${fence}\n`) && selected.endsWith(`\n${fence}`) && selected.length > fence.length * 2 + 1;

  if (isFenced) {
    const inner = selected.slice(fence.length + 1, selected.length - fence.length - 1);
    const next = text.slice(0, start) + inner + text.slice(end);
    return { text: next, start, end: start + inner.length };
  }

  const block = `${fence}\n${selected}\n${fence}`;
  const next = text.slice(0, start) + block + text.slice(end);
  const innerStart = start + fence.length + 1;
  return { text: next, start: innerStart, end: innerStart + selected.length };
}

export function insertLinkStub(text: string, start: number, end: number): MarkdownFormatResult {
  const label = text.slice(start, end);
  const urlPlaceholder = 'https://';
  const next = `${text.slice(0, start)}[${label}](${urlPlaceholder})${text.slice(end)}`;
  const caret = label.length > 0
    ? start + 1 + label.length + 2 + urlPlaceholder.length
    : start + 1;
  return { text: next, start: caret, end: caret };
}

export function setHeadingLevel(
  text: string,
  start: number,
  end: number,
  level: number | null
): MarkdownFormatResult {
  const { lineStart, lineEnd } = lineRange(text, start, end);
  const line = text.slice(lineStart, lineEnd);
  const stripped = line.replace(/^#{1,6}\s*/, '');
  const nextLine = level ? `${'#'.repeat(level)} ${stripped}` : stripped;
  const next = text.slice(0, lineStart) + nextLine + text.slice(lineEnd);
  const delta = nextLine.length - line.length;
  return { text: next, start: Math.max(lineStart, start + delta), end: end + delta };
}
